import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import StockManager from '../components/StockManager';
import SalesAnalytics from '../components/SalesAnalytics';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import html2canvas from 'html2canvas';
import ReceiptModal from '../components/ReceiptModal';
import { io } from 'socket.io-client';
import './AdminDashboard.css';
import { 
  adminGetProducts, 
  adminCreateProduct, 
  adminUpdateProduct, 
  adminDeleteProduct,  
  adminGetAllOrders,
  adminUpdateOrderStatus,
  uploadImage,
  getFullImageUrl,
  getOrderStats,
  getUserOrderHistory,
  getUserPointsHistory,
  API_URL,
  adminSearchCustomers
} from '../services/api';

interface VariationSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}
interface Variation {
  color: string;
  colorCode: string;
  images: string[];
  additionalPrice: number;
  isDefault?: boolean;
  sizes: VariationSize[];
}
interface Dimension {
  length?: number;
  breadth?: number;
  height?: number;
  unit: 'cm' | 'm' | 'inch';
}

interface AgeRange {
  min: number;
  max: number;
}
interface Product {
  _id: string;
  name: string;
  description: string;
  costPrice: number;
  price: number;
  image: string;
  images: string[];
  variations: Variation[];
  category: string;
  inStock: boolean;
  stockQuantity: number;
  isFeatured: boolean;
  isOverseas?: boolean;
  discount?: number;
  dimension?: Dimension;
  rimSize?: string;
  ageRange?: AgeRange;
  relatedCategories?: string[]; 
}
interface Order {
  _id: string;
  user: { _id?: string; name: string; email: string };
  items: any[];
  total: number;
  status: string;
  paymentMethod?: string;
  paymentStatus?: string;
  createdAt: string;
  updatedAt?: string;
  orderReference: string;
  shippingAddress?: { 
    fullName: string;
    address: string;
    city: string;
    state: string;
    postalCode?: string;
    phone: string;
  };
  isPartPayment?: boolean;
  partPaymentStatus?: 'pending' | 'partial' | 'completed' | 'cancelled' | 'balance_pending';
  remainingBalance?: number;
  totalPaid?: number;
  partPaymentDeposit?: number;
  depositConfirmed?: boolean; 
  depositConfirmedAt?: string; 
  balancePaymentRequested?: boolean;
  balancePaymentRequestedAt?: string;
  balancePaymentConfirmed?: boolean;
}
interface User {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  createdAt: string;
  lastLogin?: string;
  isActive?: boolean;
  totalSpent?: number;
  orderCount?: number;
  pointsBalance?: number;
}

interface OrderStats {
  pending: number;
  paid?: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  returned?: number;
  refunded?: number;
  total: number;
  partPaymentOrders?: number;
  partPaymentCompleted?: number;
  partPaymentPartial?: number;
  totalPartPaymentDeposits?: number;
  totalRemainingBalances?: number;
}
interface PointsTransaction {
  _id: string;
  amount: number;
  type: 'earn' | 'redeem' | 'bonus' | 'refund';
  description: string;
  createdAt: string;
}
interface UserDetailsModalProps {
  user: User | null;
  orders: Order[];
  pointsHistory: PointsTransaction[];
  loading: boolean;
  onClose: () => void;
}

const UK_SHOE_SIZES_WITH_FOOT_LENGTH: Omit<VariationSize, 'stockQuantity'>[] = [
  { ukSize: 26, footLengthCm: 16.5 },
  { ukSize: 27, footLengthCm: 17.1 },
  { ukSize: 28, footLengthCm: 17.8 },
  { ukSize: 29, footLengthCm: 18.4 },
  { ukSize: 30, footLengthCm: 19.1 },
  { ukSize: 31, footLengthCm: 19.7 },
  { ukSize: 32, footLengthCm: 20.4 },
  { ukSize: 33, footLengthCm: 21.0 },
  { ukSize: 34, footLengthCm: 21.6 },
  { ukSize: 35, footLengthCm: 22.2 },
  { ukSize: 36, footLengthCm: 22.9 },
  { ukSize: 37, footLengthCm: 23.5 },
  { ukSize: 38, footLengthCm: 24.1 },
  { ukSize: 39, footLengthCm: 24.8 },
  { ukSize: 40, footLengthCm: 25.4 },
  { ukSize: 41, footLengthCm: 26.0 },
  { ukSize: 42, footLengthCm: 26.7 },
  { ukSize: 43, footLengthCm: 27.3 },
  { ukSize: 44, footLengthCm: 27.9 },
  { ukSize: 45, footLengthCm: 28.6 }
];

const formatPrice = (price: number | undefined | null): string => {
  if (!price && price !== 0) return '0';
  const numPrice = Math.round(Number(price));
  return numPrice.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// User Details Modal Component
const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ 
  user, 
  orders, 
  pointsHistory, 
  loading, 
  onClose 
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'orders' | 'points'>('orders');

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  if (!user) return null;

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: '#ff9800',
      paid: '#9c27b0',
      shipped: '#00bcd4',
      delivered: '#4caf50',
      cancelled: '#f44336',
      returned: '#ff5722',
      refunded: '#607d8b'
    };
    return colors[status] || '#757575';
  };

  const getStatusText = (status: string) => {
    const texts: { [key: string]: string } = {
      pending: 'Pending Payment',
      paid: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      returned: 'Returned',
      refunded: 'Refunded'
    };
    return texts[status] || status;
  };

  const getTransactionIcon = (type: string) => {
    switch(type) {
      case 'earn': return '⭐';
      case 'redeem': return '🛒';
      case 'bonus': return '🎁';
      case 'refund': return '↩️';
      default: return '📝';
    }
  };

  const getTransactionColor = (type: string) => {
    switch(type) {
      case 'earn': return '#10b981';
      case 'redeem': return '#f59e0b';
      case 'bonus': return '#8b5cf6';
      case 'refund': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="user-details-modal-overlay" onClick={onClose}>
      <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="user-info-header">
            <div className="user-basic-info">
              <h2>{user.name}</h2>
              <p className="user-email">{user.email}</p>
              <div className="user-stats-badges">
                <span className="stat-badge">📦 {user.orderCount || orders.length} orders</span>
                <span className="stat-badge">💰 ₦{(user.totalSpent || orders.reduce((sum, o) => sum + o.total, 0)).toLocaleString()}</span>
                <span className="stat-badge">⭐ {user.pointsBalance || pointsHistory.reduce((sum, t) => 
                  t.type === 'earn' || t.type === 'bonus' ? sum + t.amount : 
                  t.type === 'redeem' || t.type === 'refund' ? sum - t.amount : sum, 0
                )} points</span>
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="user-details-tabs">
          <button 
            className={`sub-tab ${activeSubTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('orders')}
          >
            📋 Order History ({orders.length})
          </button>
          <button 
            className={`sub-tab ${activeSubTab === 'points' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('points')}
          >
            ⭐ Points History ({pointsHistory.length})
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : activeSubTab === 'orders' ? (
            <div className="user-orders-list">
              {orders.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <p>No orders yet</p>
                </div>
              ) : (
                orders.map((order) => (
                  <div key={order._id} className="user-order-card">
                    <div className="order-card-header">
                      <span className="admin-order-id">Order #{order.orderReference || order._id.slice(-8)}</span>
                      <span className="admin-order-date">{new Date(order.createdAt).toLocaleDateString()}</span>
                      <span className="order-status" style={{ backgroundColor: getStatusColor(order.status), color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    <div className="order-card-body">
                      <div className="order-items-summary">
                        {order.items.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="order-item-summary">
                            <span>{item.name} x{item.quantity}</span>
                          </div>
                        ))}
                        {order.items.length > 2 && (
                          <span className="more-items">+{order.items.length - 2} more</span>
                        )}
                      </div>
                      <div className="order-total">
                        <strong>Total: ₦{order.total.toLocaleString()}</strong>
                      </div>
                      <div className="order-payment-info">
                        <span className={`payment-status ${order.paymentStatus}`}>
                          {order.paymentStatus === 'paid'
                            ? '✓ Paid'
                            : order.paymentStatus === 'partial' || order.paymentStatus === 'deposit_paid'
                              ? '💰 Deposit Paid'
                              : order.paymentStatus === 'failed'
                                ? '❌ Payment Failed'
                                : '⏳ Pending'}
                        </span>
                        <span className="payment-method">
                          💳 Paystack
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="user-points-list">
              {pointsHistory.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">⭐</div>
                  <p>No points transactions yet</p>
                </div>
              ) : (
                pointsHistory.map((transaction) => (
                  <div key={transaction._id} className="points-transaction-card">
                    <div className="transaction-icon" style={{ backgroundColor: getTransactionColor(transaction.type) + '20' }}>
                      <span>{getTransactionIcon(transaction.type)}</span>
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-description">{transaction.description}</div>
                      <div className="transaction-date">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className={`transaction-amount ${transaction.type === 'earn' || transaction.type === 'bonus' ? 'positive' : 'negative'}`}>
                      {transaction.type === 'earn' || transaction.type === 'bonus' ? '+' : '-'}{transaction.amount} pts
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Image-dimension validation
const MIN_IMAGE_WIDTH = 800;
const MIN_IMAGE_HEIGHT = 800;

const validateImageDimensions = (
  file: File
): Promise<{ valid: boolean; message?: string }> => {
  return new Promise((resolve) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const { width, height } = image;

      URL.revokeObjectURL(objectUrl);

      if (
        width < MIN_IMAGE_WIDTH ||
        height < MIN_IMAGE_HEIGHT
      ) {
        resolve({
          valid: false,
          message:
            `Image resolution is too small. ` +
            `Please upload an image at least ` +
            `${MIN_IMAGE_WIDTH} × ${MIN_IMAGE_HEIGHT} pixels. ` +
            `Your image is ${width} × ${height} pixels.`,
        });

        return;
      }

      resolve({ valid: true });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      resolve({
        valid: false,
        message:
          'Unable to read the image dimensions. Please select another image.',
      });
    };

    image.src = objectUrl;
  });
};

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'orders' | 'users' | 'stock' | 'sales'>('overview');
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [orderStats, setOrderStats] = useState<OrderStats>({
    pending: 0,
    paid: 0, 
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    returned: 0,
    refunded: 0,
    total: 0,
    partPaymentOrders: 0,
    partPaymentCompleted: 0,
    partPaymentPartial: 0,
    totalPartPaymentDeposits: 0,
    totalRemainingBalances: 0
  });
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedOrderForModal, setSelectedOrderForModal] = useState<Order | null>(null);
  const [receiptOrder, setReceiptOrder] = useState<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Customer lookup state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchPerformed, setCustomerSearchPerformed] = useState(false);

  // Server-side product browsing
  const [productPage, setProductPage] = useState(1);
  const [productPagination, setProductPagination] = useState({
    page: 1,
    limit: 20,
    totalProducts: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [appliedProductSearchTerm, setAppliedProductSearchTerm] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productStockFilter, setProductStockFilter] = useState('all');
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  // Server-side order browsing
  const [orderPage, setOrderPage] = useState(1);
  const [orderPagination, setOrderPagination] = useState({
    page: 1,
    limit: 20,
    totalOrders: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [appliedOrderSearchTerm, setAppliedOrderSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderPaymentStatusFilter, setOrderPaymentStatusFilter] = useState('all');
  const [orderStartDate, setOrderStartDate] = useState('');
  const [orderEndDate, setOrderEndDate] = useState('');
  
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);

// Background refresh (silent, no button spinner)
  const [productRefreshing, setProductRefreshing] = useState(false);
  const [orderRefreshing, setOrderRefreshing] = useState(false);

  // Refs holding current filter values so fetch callbacks never need to change
  const productFiltersRef = useRef({
    search: '',
    category: 'all',
    stock: 'all',
  });

  const orderFiltersRef = useRef({
    search: '',
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
  });

  // User details modal state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserOrders, setSelectedUserOrders] = useState<Order[]>([]);
  const [selectedUserPoints, setSelectedUserPoints] = useState<PointsTransaction[]>([]);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  // Product Type Selection
  const [productType, setProductType] = useState<'simple' | 'color_only' | 'color_size'>('simple');

  // Pricing state
  const [costPrice, setCostPrice] = useState('');
  const [profitMargin, setProfitMargin] = useState<number>(30);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [manualPriceMode, setManualPriceMode] = useState(false);
  const [manualPrice, setManualPrice] = useState('');

  // Variation states
  const [variations, setVariations] = useState<Variation[]>([]);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [editingVariationIndex, setEditingVariationIndex] = useState<number | null>(null);
  const [variationForm, setVariationForm] = useState({
    color: '',
    colorCode: '#000000',
    additionalPrice: '',
    isDefault: false,
    images: [] as File[]
  });
  const [variationImagePreviews, setVariationImagePreviews] = useState<string[]>([]);
  
  // Size management for the variation being edited
  const [variationSizes, setVariationSizes] = useState<VariationSize[]>([]);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [editingSizeIndex, setEditingSizeIndex] = useState<number | null>(null);
  const [selectedSizeToAdd, setSelectedSizeToAdd] = useState<string>('');
  const [sizeForm, setSizeForm] = useState({
    ukSize: 0,
    footLengthCm: 0,
    stockQuantity: 0
  });

  // Dimension state
  const [dimension, setDimension] = useState<Dimension>({
    length: undefined,
    breadth: undefined,
    height: undefined,
    unit: 'cm'
  });
  const [dimensionEnabled, setDimensionEnabled] = useState({
    length: false,
    breadth: false,
    height: false
  });

  // Rim size
  const [rimSize, setRimSize] = useState<string>('');

  // Age range
  const [ageRange, setAgeRange] = useState<AgeRange>({
    min: 0,
    max: 100
  });
  const [enableAgeRange, setEnableAgeRange] = useState<boolean>(false);
  const [relatedCategories, setRelatedCategories] = useState<string[]>([]);

  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    category: 'shoes',
    inStock: true,
    stockQuantity: '',
    isFeatured: false,
    isOverseas: false,
    discount: ''
  });

  const calculateSellingPrice = useCallback((cost: number, margin: number): number => {
    return cost / (1 - (margin / 100));
  }, []);

  useEffect(() => {
    const cost = parseFloat(costPrice);
    if (!isNaN(cost) && cost > 0 && !manualPriceMode) {
      const newPrice = calculateSellingPrice(cost, profitMargin);
      setCalculatedPrice(Math.round(newPrice));
    }
  }, [costPrice, profitMargin, calculateSellingPrice, manualPriceMode]);

  const handleProfitMarginChange = (margin: number) => {
    setProfitMargin(margin);
    setManualPriceMode(false);
  };

  const handleCostPriceChange = (value: string) => {
    setCostPrice(value);
    setManualPriceMode(false);
  };

  const handleManualPriceChange = (value: string) => {
    setManualPrice(value);
    setManualPriceMode(true);
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    setIsMobile(mq.matches);

    // Fallback for older Safari
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      // @ts-ignore
      mq.addListener(handler);
      // @ts-ignore
      return () => mq.removeListener(handler);
    }
  }, []);

  const truncateText = (text: string, desktopLength: number = 30, mobileLength: number = 15): string => {
    if (!text) return '';
    const maxLength = isMobile ? mobileLength : desktopLength;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Fetch lightweight order statistics only. This does not load orders.
  const fetchOrderStats = useCallback(async () => {
    try {
      const response = await getOrderStats();
      if (response.data.success && response.data.stats) {
        setOrderStats(prev => ({
          ...prev,
          pending: response.data.stats.pending || 0,
          paid: response.data.stats.paid || 0,
          shipped: response.data.stats.shipped || 0,
          delivered: response.data.stats.delivered || 0,
          cancelled: response.data.stats.cancelled || 0,
          returned: response.data.stats.returned || 0,
          refunded: response.data.stats.refunded || 0,
          total: response.data.stats.total || 0,
          partPaymentOrders: response.data.stats.partPaymentOrders || 0,
          partPaymentCompleted: response.data.stats.partPaymentCompleted || 0,
          partPaymentPartial: response.data.stats.partPaymentPartial || 0,
          totalPartPaymentDeposits: response.data.stats.totalPartPaymentDeposits || 0,
          totalRemainingBalances: response.data.stats.totalRemainingBalances || 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch order stats:', error);
    }
  }, []);

  const fetchProducts = useCallback(async (
    page = 1,
    options: { silent?: boolean } = {}
  ) => {
    if (options.silent) {
      setProductRefreshing(true);
    } else {
      setProductSearchLoading(true);
    }

    try {
      const { search, category, stock } = productFiltersRef.current;

      const response = await adminGetProducts({
        page,
        limit: 20,
        search: search.trim(),
        category,
        stock,
      });

      if (response.data.success) {
        const productsData = response.data.products || [];
        const updatedProducts = productsData.map((product: Product) => ({
          ...product,
          inStock:
            product.variations?.some(v =>
              v.sizes?.some(s => s.stockQuantity > 0)
            ) || (product.stockQuantity || 0) > 0,
        }));

        setProducts(updatedProducts);

        setProductPagination(
          response.data.pagination || {
            page,
            limit: 20,
            totalProducts: productsData.length,
            totalPages: productsData.length > 0 ? 1 : 0,
            hasNextPage: false,
            hasPreviousPage: page > 1,
          }
        );
      } else {
        setProducts([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    } finally {
      if (options.silent) {
        setProductRefreshing(false);
      } else {
        setProductSearchLoading(false);
      }
    }
  }, []);   // ✅ empty deps — stable forever

  const fetchOrders = useCallback(async (
    page = 1,
    options: { silent?: boolean } = {}
  ) => {
    if (options.silent) {
      setOrderRefreshing(true);
    } else {
      setOrderSearchLoading(true);
    }

    try {
      const { search, status, paymentStatus, startDate, endDate } =
        orderFiltersRef.current;

      const response = await adminGetAllOrders({
        page,
        limit: 20,
        search: search.trim(),
        status,
        paymentStatus,
        startDate,
        endDate,
      });

      if (response.data.success) {
        const transformedOrders = (response.data.orders || []).map(
          (order: any) => ({
            ...order,
            orderReference: order.orderReference || order._id.slice(-8),
          })
        );

        setOrders(transformedOrders);

        setOrderPagination(
          response.data.pagination || {
            page,
            limit: 20,
            totalOrders: transformedOrders.length,
            totalPages: transformedOrders.length > 0 ? 1 : 0,
            hasNextPage: false,
            hasPreviousPage: page > 1,
          }
        );
      } else {
        setOrders([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      if (options.silent) {
        setOrderRefreshing(false);
      } else {
        setOrderSearchLoading(false);
      }
    }
  }, []);   // ✅ empty deps — stable forever

  const handleUpdateOrderStatus = async (orderId: string, status: string) => {
    try {
      await adminUpdateOrderStatus(orderId, status);

      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId
            ? { ...order, status }
            : order
        )
      );

      await fetchOrderStats();
    } catch (error: any) {
      console.error('Failed to update order status:', error);
      alert(
        error.response?.data?.error ||
        'Failed to update order status. Please try again.'
      );
    }
  };

  const searchCustomers = useCallback(async (searchTerm: string) => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) {
      setUsers([]);
      setCustomerSearchPerformed(false);
      return;
    }
    setCustomerSearchLoading(true);
    setCustomerSearchPerformed(true);
    try {
      const response = await adminSearchCustomers(trimmedSearch);
      if (response.data.success) setUsers(response.data.customers || []);
      else setUsers([]);
    } catch (error: any) {
      console.error('Failed to search customers:', error);
      setUsers([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, []);

  const handleCustomerSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await searchCustomers(userSearchTerm);
  };

  // Dashboard opens with lightweight statistics only. Collections are loaded on demand.
  // Refs let the 30-second refresh always use the current tab/page without
  // recreating the interval every time the administrator changes tabs or pages.
  const activeTabRef = useRef(activeTab);
  const orderPageRef = useRef(orderPage);
  const productPageRef = useRef(productPage);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    orderPageRef.current = orderPage;
  }, [orderPage]);

  useEffect(() => {
    productPageRef.current = productPage;
  }, [productPage]);

  // Keep product filter refs in sync with state
  useEffect(() => {
    productFiltersRef.current = {
      search: appliedProductSearchTerm,
      category: productCategoryFilter,
      stock: productStockFilter,
    };
  }, [appliedProductSearchTerm, productCategoryFilter, productStockFilter]);

  // Keep order filter refs in sync with state
  useEffect(() => {
    orderFiltersRef.current = {
      search: appliedOrderSearchTerm,
      status: orderStatusFilter,
      paymentStatus: orderPaymentStatusFilter,
      startDate: orderStartDate,
      endDate: orderEndDate,
    };
  }, [
    appliedOrderSearchTerm,
    orderStatusFilter,
    orderPaymentStatusFilter,
    orderStartDate,
    orderEndDate,
  ]);

  useEffect(() => {
    fetchOrderStats();

    intervalRef.current = setInterval(() => {
      // Only poll stats while on the tabs that display them
      if (
        activeTabRef.current === 'overview' ||
        activeTabRef.current === 'orders'
      ) {
        fetchOrderStats();
      }

      if (activeTabRef.current === 'orders') {
        fetchOrders(orderPageRef.current, { silent: true });
      }

      if (activeTabRef.current === 'products') {
        fetchProducts(productPageRef.current, { silent: true });
      }
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);   // ✅ runs once, uses refs for current values

  // Refetch when switching tabs
  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts(productPage, { silent: false });
    } else if (activeTab === 'orders') {
      fetchOrders(orderPage, { silent: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Refetch when the page changes (silent, so the button doesn't flash)
  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts(productPage, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPage]);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders(orderPage, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPage]);

  // Refetch when filters change (show button spinner only when the user submitted)
  useEffect(() => {
    if (activeTab === 'products') {
      if (productPage !== 1) {
        setProductPage(1);
        return;
      }
      fetchProducts(1, { silent: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedProductSearchTerm, productCategoryFilter, productStockFilter]);

  useEffect(() => {
    if (activeTab === 'orders') {
      if (orderPage !== 1) {
        setOrderPage(1);
        return;
      }
      fetchOrders(1, { silent: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedOrderSearchTerm,
    orderStatusFilter,
    orderPaymentStatusFilter,
    orderStartDate,
    orderEndDate,
  ]);

  const handleViewUserDetails = async (user: User) => {
    if (loadingUserDetails) return;   // prevent double-click

    setSelectedUser(user);
    setLoadingUserDetails(true);

    const [ordersResult, pointsResult] = await Promise.allSettled([
      getUserOrderHistory(user._id),
      getUserPointsHistory(user._id),
    ]);

    if (
      ordersResult.status === 'fulfilled' &&
      ordersResult.value.data.success
    ) {
      setSelectedUserOrders(ordersResult.value.data.orders || []);
    } else {
      setSelectedUserOrders([]);
    }

    if (
      pointsResult.status === 'fulfilled' &&
      pointsResult.value.data.success
    ) {
      setSelectedUserPoints(pointsResult.value.data.transactions || []);
    } else {
      setSelectedUserPoints([]);
    }

    setLoadingUserDetails(false);
  };

  const handleImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Please select an image under 5MB.');

      e.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');

      e.target.value = '';
      return;
    }

    // Validate image dimensions
    const dimensionValidation = await validateImageDimensions(file);

    if (!dimensionValidation.valid) {
      alert(
        dimensionValidation.message ||
          'Image dimensions are not valid.'
      );

      e.target.value = '';
      return;
    }

    // Save the validated image
    setSelectedImage(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };

    reader.readAsDataURL(file);
  };

  const handleImageUpload = async (): Promise<string> => {
    if (!selectedImage) return '';
    
    const formData = new FormData();
    formData.append('image', selectedImage);
    formData.append('category', 'products');
    
    try {
      const response = await uploadImage(formData, 'products');
      let imageUrl = '';
      
      if (response.data.imageUrl) {
        imageUrl = response.data.imageUrl;
        console.log('✅ Uploaded to Cloudinary:', imageUrl);
      } else if (response.data.url) {
        imageUrl = response.data.url;
      } else if (response.data.filename) {
        imageUrl = `/uploads/products/${response.data.filename}`;
        console.warn('⚠️ Using local path instead of Cloudinary:', imageUrl);
      }
      return imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Image upload failed. Please try again.');
      return '';
    }
  };

  const handleAddVariation = () => {
    setEditingVariationIndex(null);
    setVariationForm({
      color: '',
      colorCode: '#000000',
      additionalPrice: '',
      isDefault: false,
      images: [] as File[]
    });
    setVariationImagePreviews([]);
    setVariationSizes([]);
    setShowVariationModal(true);
  };

  const handleEditVariation = (index: number) => {
    const variation = variations[index];
    setEditingVariationIndex(index);
    setVariationForm({
      color: variation.color,
      colorCode: variation.colorCode,
      additionalPrice: variation.additionalPrice.toString(),
      isDefault: variation.isDefault || false,
      images: [] as File[]
    });
    setVariationImagePreviews(variation.images);
    setVariationSizes(variation.sizes || []);
    setShowVariationModal(true);
  };

  const handleRemoveVariation = (index: number) => {
    if (window.confirm('Remove this color variation?')) {
      setVariations(variations.filter((_, i) => i !== index));
    }
  };

  const handleVariationImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    const invalidImages: string[] = [];

    for (const file of files) {
      // Validate file size
      if (file.size > 5 * 1024 * 1024) {
        invalidImages.push(
          `${file.name}: File size exceeds 5MB.`
        );
        continue;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        invalidImages.push(
          `${file.name}: Invalid image file type.`
        );
        continue;
      }

      // Validate image dimensions
      const dimensionValidation = await validateImageDimensions(file);

      if (!dimensionValidation.valid) {
        invalidImages.push(
          `${file.name}: ${
            dimensionValidation.message ||
            'Image dimensions are too small.'
          }`
        );
        continue;
      }

      validFiles.push(file);
    }

    // Display validation errors for rejected files
    if (invalidImages.length > 0) {
      alert(
        `Some images were rejected:\n\n${invalidImages.join('\n\n')}`
      );
    }

    // Stop if no images passed validation
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Add only validated files
    setVariationForm((previousForm) => ({
      ...previousForm,
      images: [
        ...previousForm.images,
        ...validFiles,
      ],
    }));

    // Generate previews only for validated files
    validFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        setVariationImagePreviews((previousPreviews) => [
          ...previousPreviews,
          reader.result as string,
        ]);
      };

      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const removeVariationImage = (index: number) => {
    setVariationForm({
      ...variationForm,
      images: variationForm.images.filter((_, i) => i !== index)
    });
    setVariationImagePreviews(variationImagePreviews.filter((_, i) => i !== index));
  };

  const handleOpenAddSizeModal = () => {
    if (!selectedSizeToAdd) {
      alert('Please select a size from the dropdown first');
      return;
    }
    
    const sizeInfo = UK_SHOE_SIZES_WITH_FOOT_LENGTH.find(s => s.ukSize === parseInt(selectedSizeToAdd));
    if (sizeInfo) {
      setEditingSizeIndex(null);
      setSizeForm({
        ukSize: sizeInfo.ukSize,
        footLengthCm: sizeInfo.footLengthCm,
        stockQuantity: 0
      });
      setShowSizeModal(true);
    } else {
      alert('Invalid size selected');
    }
  };

  const handleEditSize = (index: number) => {
    const size = variationSizes[index];
    setEditingSizeIndex(index);
    setSizeForm({
      ukSize: size.ukSize,
      footLengthCm: size.footLengthCm,
      stockQuantity: size.stockQuantity
    });
    setShowSizeModal(true);
  };

  const handleSaveSize = () => {
    if (sizeForm.stockQuantity < 0) {
      alert('Stock quantity cannot be negative');
      return;
    }

    const newSize: VariationSize = {
      ukSize: sizeForm.ukSize,
      footLengthCm: sizeForm.footLengthCm,
      stockQuantity: sizeForm.stockQuantity
    };

    setVariationSizes(prevSizes => {
      let updatedSizes = [...prevSizes];
      
      if (editingSizeIndex !== null) {
        updatedSizes[editingSizeIndex] = newSize;
      } else {
        const sizeExists = updatedSizes.some(s => s.ukSize === newSize.ukSize);
        if (sizeExists) {
          alert(`UK size ${newSize.ukSize} has already been added`);
          return prevSizes;
        }
        updatedSizes.push(newSize);
      }
      return updatedSizes;
    });

    setShowSizeModal(false);
    setSelectedSizeToAdd('');
    setSizeForm({ ukSize: 0, footLengthCm: 0, stockQuantity: 0 });
    setEditingSizeIndex(null);
  };

  const handleRemoveSize = (index: number) => {
    if (window.confirm('Remove this size?')) {
      setVariationSizes(variationSizes.filter((_, i) => i !== index));
    }
  };

  const saveVariation = async () => {
    if (!variationForm.color) {
      alert('Please enter a color name');
      return;
    }

    let imageUrls: string[] = [];
    
    if (variationImagePreviews.length > 0) {
      const validUrls = variationImagePreviews.filter(preview => 
        preview.startsWith('https://res.cloudinary.com/') || 
        preview.startsWith('http')
      );
      imageUrls.push(...validUrls);
    }
    
    if (variationForm.images.length > 0) {
      for (const image of variationForm.images) {
        const formData = new FormData();
        formData.append('image', image);
        formData.append('category', 'products');
        
        try {
          const response = await uploadImage(formData, 'products');
          let imageUrl = '';
          
          if (response.data.imageUrl) {
            imageUrl = response.data.imageUrl;
            console.log('✅ Variation image uploaded to Cloudinary:', imageUrl);
          } else if (response.data.url) {
            imageUrl = response.data.url;
          }
          
          if (imageUrl) {
            imageUrls.push(imageUrl);
          } else {
            throw new Error('No image URL returned');
          }
        } catch (error) {
          console.error('Failed to upload variation image:', error);
          alert('Failed to upload one or more images. Please try again.');
          return;
        }
      }
    }

    const newVariation: Variation = {
      color: variationForm.color,
      colorCode: variationForm.colorCode,
      images: imageUrls,
      additionalPrice: parseFloat(variationForm.additionalPrice) || 0,
      isDefault: variationForm.isDefault,
      sizes: variationSizes
    };

    if (editingVariationIndex !== null) {
      const updatedVariations = [...variations];
      updatedVariations[editingVariationIndex] = newVariation;
      setVariations(updatedVariations);
    } else {
      setVariations([...variations, newVariation]);
    }

    setShowVariationModal(false);
    setVariationForm({
      color: '',
      colorCode: '#000000',
      additionalPrice: '',
      isDefault: false,
      images: [] as File[]
    });
    setVariationImagePreviews([]);
    setVariationSizes([]);
  };

  const handleDimensionChange = (field: 'length' | 'breadth' | 'height', value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    setDimension({
      ...dimension,
      [field]: numValue
    });
  };

  const handleDimensionUnitChange = (unit: 'cm' | 'm' | 'inch') => {
    setDimension({
      ...dimension,
      unit
    });
  };

  const handleAgeRangeChange = (field: 'min' | 'max', value: string) => {
    const numValue = parseInt(value) || 0;
    setAgeRange({
      ...ageRange,
      [field]: numValue
    });
  };

  const getTotalStock = (product: Product) => {
    if (product.variations && product.variations.length > 0) {
      return product.variations.reduce((sum, v) => 
        sum + (v.sizes?.reduce((sizeSum, s) => sizeSum + s.stockQuantity, 0) || 0), 0);
    }
    return product.stockQuantity || 0;
  };

  const getStockStatus = (product: Product) => {
    const totalStock = getTotalStock(product);
    
    if (totalStock === 0) {
      return <span style={{ color: '#f44336' }}>❌ Out of Stock</span>;
    } else if (totalStock < 10) {
      return <span style={{ color: '#ff9800' }}>⚠️ Low Stock ({totalStock})</span>;
    } else {
      return <span style={{ color: '#4caf50' }}>✅ In Stock ({totalStock})</span>;
    }
  };

  const getProfitMarginDisplay = (product: Product) => {
    if (product.costPrice && product.price) {
      const margin = ((product.price - product.costPrice) / product.price) * 100;
      return (
        <span className={`profit-margin ${margin >= 30 ? 'high' : margin >= 15 ? 'medium' : 'low'}`}>
          {margin.toFixed(1)}%
        </span>
      );
    }
    return <span className="profit-margin unknown">—</span>;
  };

  const getVariationBadges = (variations: Variation[]) => {
    if (!variations || variations.length === 0) return null;
    return (
      <div className="variation-badges">
        {variations.map((v, idx) => (
          <span 
            key={idx} 
            className="variation-badge"
            style={{ backgroundColor: v.colorCode, color: '#fff' }}
            title={`${v.color}: ${v.sizes?.length || 0} sizes`}
          >
            {v.color}
          </span>
        ))}
      </div>
    );
  };

  const pendingOrdersCount = (orderStats.pending || 0) + (orderStats.paid || 0);

  const resetForm = () => {
    setProductForm({
      name: '',
      description: '',
      category: 'shoes',
      inStock: true,
      stockQuantity: '',
      isFeatured: false,
      isOverseas: false,
      discount: ''
    });
    setEditingProduct(null);
    setSelectedImage(null);
    setImagePreview('');
    setVariations([]);
    setDimension({ length: undefined, breadth: undefined, height: undefined, unit: 'cm' });
    setDimensionEnabled({ length: false, breadth: false, height: false });
    setRimSize('');
    setAgeRange({ min: 0, max: 100 });
    setEnableAgeRange(false);
    setRelatedCategories([]);
    setProductType('simple');
    setCostPrice('');
    setProfitMargin(30);
    setCalculatedPrice(0);
    setManualPriceMode(false);
    setManualPrice('');
  };

  const editProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      category: product.category,
      inStock: product.inStock,
      stockQuantity: (product.stockQuantity || 0).toString(),
      isFeatured: product.isFeatured || false,
      isOverseas: product.isOverseas || false,
      discount: (product.discount || 0).toString()
    });
    
    setImagePreview(getFullImageUrl(product.image));
    
    setCostPrice(product.costPrice?.toString() || '');
    if (product.price) {
      setManualPrice(product.price.toString());
      setManualPriceMode(true);
      if (product.costPrice && product.costPrice > 0) {
        const margin = ((product.price - product.costPrice) / product.price) * 100;
        setProfitMargin(Math.round(margin));
      }
    }
    
    const hasColorVariations = product.variations && product.variations.length > 0;
    const hasSizes = product.variations?.some(v => v.sizes && v.sizes.length > 0);
    
    if (hasColorVariations && hasSizes) {
      setProductType('color_size');
    } else if (hasColorVariations) {
      setProductType('color_only');
    } else {
      setProductType('simple');
    }
    
    if (product.relatedCategories && product.relatedCategories.length > 0) {
      setRelatedCategories(product.relatedCategories);
    } else {
      setRelatedCategories([]);
    }
    
    if (product.variations && product.variations.length > 0) {
      setVariations(product.variations);
    } else {
      setVariations([]);
    }

    if (product.dimension) {
      setDimension({
        length: product.dimension.length,
        breadth: product.dimension.breadth,
        height: product.dimension.height,
        unit: product.dimension.unit || 'cm'
      });
      setDimensionEnabled({
        length: product.dimension.length !== undefined && product.dimension.length > 0,
        breadth: product.dimension.breadth !== undefined && product.dimension.breadth > 0,
        height: product.dimension.height !== undefined && product.dimension.height > 0
      });
    } else {
      setDimension({ length: undefined, breadth: undefined, height: undefined, unit: 'cm' });
      setDimensionEnabled({ length: false, breadth: false, height: false });
    }

    if (product.rimSize) {
      setRimSize(product.rimSize);
    } else {
      setRimSize('');
    }

    if (product.ageRange) {
      setAgeRange(product.ageRange);
      setEnableAgeRange(true);
    } else {
      setAgeRange({ min: 0, max: 100 });
      setEnableAgeRange(false);
    }
    
    setShowProductModal(true);
  };

  const handleStockQuantityChange = (value: string) => {
    const qty = parseInt(value) || 0;
    setProductForm({
      ...productForm,
      stockQuantity: value,
      inStock: qty > 0
    });
  };

  const handleSaveProduct = async () => {
    const finalCostPrice = parseFloat(costPrice);
    if (isNaN(finalCostPrice) || finalCostPrice <= 0) {
      alert('Please enter a valid cost price');
      return;
    }

    let finalPrice: number;
    if (manualPriceMode && manualPrice) {
      finalPrice = parseFloat(manualPrice);
      if (isNaN(finalPrice) || finalPrice <= 0) {
        alert('Please enter a valid selling price');
        return;
      }
    } else {
      finalPrice = calculatedPrice;
      if (finalPrice <= 0) {
        alert('Please enter a valid cost price or profit margin');
        return;
      }
    }

    let imageUrl = editingProduct?.image || '';
    
    if (variations.length > 0) {
      const defaultVariation = variations.find(v => v.isDefault) || variations[0];
      if (defaultVariation.images && defaultVariation.images.length > 0) {
        imageUrl = defaultVariation.images[0];
      }
    } else if (selectedImage) {
      imageUrl = await handleImageUpload();
    }

    let finalVariations = [...variations];
    let stockQty = 0;
    let inStockStatus = true;
    
    switch(productType) {
      case 'simple':
        stockQty = parseInt(productForm.stockQuantity) || 0;
        inStockStatus = stockQty > 0;
        finalVariations = [];
        break;
      case 'color_only':
        stockQty = 0;
        inStockStatus = variations.some(v => v.sizes?.[0]?.stockQuantity > 0);
        break;
      case 'color_size':
        stockQty = 0;
        inStockStatus = variations.some(v => v.sizes?.some(s => s.stockQuantity > 0));
        break;
    }

    const finalDimension: Dimension = { unit: dimension.unit };
    if (dimensionEnabled.length) finalDimension.length = dimension.length;
    if (dimensionEnabled.breadth) finalDimension.breadth = dimension.breadth;
    if (dimensionEnabled.height) finalDimension.height = dimension.height;
    
    const hasDimension = (dimensionEnabled.length && dimension.length) ||
                        (dimensionEnabled.breadth && dimension.breadth) ||
                        (dimensionEnabled.height && dimension.height);

    const finalDiscount = productForm.discount ? parseFloat(productForm.discount) : 0;
    const discountedPrice = finalDiscount > 0 ? finalPrice * (1 - finalDiscount / 100) : finalPrice;

    const productData = {
      name: productForm.name,
      description: productForm.description,
      costPrice: finalCostPrice,
      price: discountedPrice,
      originalPrice: finalPrice,
      category: productForm.category,
      inStock: inStockStatus,
      stockQuantity: stockQty,
      isFeatured: productForm.isFeatured,
      isOverseas: productForm.isOverseas,
      discount: finalDiscount,
      image: imageUrl,
      variations: finalVariations,
      dimension: hasDimension ? finalDimension : undefined,
      rimSize: productForm.category === 'travel' ? rimSize : undefined,
      ageRange: enableAgeRange ? ageRange : undefined,
      relatedCategories: relatedCategories
    };

    try {
      if (editingProduct) {
        await adminUpdateProduct(editingProduct._id, productData);
        alert('Product updated!');
      } else {
        await adminCreateProduct(productData);
        alert('Product created!');
      }
      setShowProductModal(false);
      resetForm();
      if (productPage === 1) {
        fetchProducts(1, { silent: true });
      } else {
        setProductPage(1);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Delete this product?')) {
      try {
        await adminDeleteProduct(productId);
        alert('Product deleted!');
        fetchProducts(productPage, { silent: true });
      } catch (error: any) {
        alert(error.response?.data?.error || 'Failed to delete product');
      }
    }
  };

  // Modal cleanup effect
  useEffect(() => {
    if (selectedOrderForModal) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [selectedOrderForModal]);

  // ============================================
  // SOCKET CONNECTION - MUST BE AT THE VERY END
  // ============================================
  
  // Create refs for the functions that the socket needs
  const fetchOrdersRef = useRef(fetchOrders);
  const fetchOrderStatsRef = useRef(fetchOrderStats);
  const notificationDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchProductsRef = useRef(fetchProducts);

  // Update refs when functions change
  useEffect(() => {
    fetchOrdersRef.current = fetchOrders;
    fetchOrderStatsRef.current = fetchOrderStats;
    fetchProductsRef.current = fetchProducts;
  }, [fetchOrders, fetchOrderStats, fetchProducts]);

  // Socket connection - stable, only runs once
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('⚠️ Admin: No token found, skipping socket connection');
      return;
    }

    console.log('🔌 Admin: Connecting to socket with role: admin');
    const socketUrl = API_URL.replace('/api', '');
    console.log('🔌 Admin: Socket URL:', socketUrl);

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      query: { token, role: 'admin' }
    });

    socket.on('connect', () => {
      console.log('✅ Admin: Socket connected successfully!');
      console.log('✅ Admin: Socket ID:', socket.id);
    });

    socket.on('admin_notification', (notification) => {
      console.log('🔔🔔🔔 Admin: Received admin notification:', notification);

      // Debounce bursts of notifications into a single refresh
      if (notificationDebounceRef.current) {
        clearTimeout(notificationDebounceRef.current);
      }

      notificationDebounceRef.current = setTimeout(() => {
        fetchOrderStatsRef.current();

        if (activeTabRef.current === 'orders') {
          fetchOrdersRef.current(undefined, { silent: true });
        }

        if (activeTabRef.current === 'products') {
          fetchProductsRef.current(undefined, { silent: true });
        }
      }, 500);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Admin: Socket connection error:', error);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Admin: Socket disconnected, reason:', reason);
    });

    return () => {
      if (notificationDebounceRef.current) {
        clearTimeout(notificationDebounceRef.current);
        notificationDebounceRef.current = null;
      }
      console.log('🔌 Admin: Cleaning up socket connection');
      socket.disconnect();
    };
  }, []);

  // ============================================
  // END OF SOCKET CONNECTION CODE
  // ============================================

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>ADMIN DASHBOARD</h1>       
        <div className="admin-tabs">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>📊 Overview</button>
          <button className={activeTab === 'products' ? 'active' : ''} onClick={() => setActiveTab('products')}>📦 Products{productPagination.totalProducts > 0 ? ` (${productPagination.totalProducts})` : ''}</button>
          <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>📋 Orders {pendingOrdersCount > 0 && <span className="tab-badge">{pendingOrdersCount}</span>}</button>
          <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>👥 Customers</button>
          <button className={activeTab === 'stock' ? 'active' : ''} onClick={() => setActiveTab('stock')}>📦 Inventory</button>
          <button className={activeTab === 'sales' ? 'active' : ''} onClick={() => setActiveTab('sales')}>📊 Analytics</button>
        </div>
         
        <div className="admin-actions">
          {activeTab === 'products' && (<button className="add-product-btn" onClick={() => setShowProductModal(true)}>+ Add Product</button>)}
          <Link to="/admin/chats" className="admin-nav-item"><span className="nav-icon">💬</span>Live Support Chats</Link>
        </div>
        
        {activeTab === 'overview' && (
          <div className="admin-overview">
            <div className="overview-header">
              <div>
                <h2>Dashboard Overview</h2>
                <p>Live operational summary. Detailed collections are loaded only when you open their tabs.</p>
              </div>
            </div>
            <div className="overview-stat-grid">
              <div className="stat-card pending"><span className="stat-icon">⏳</span><span className="stat-value">{orderStats.pending}</span><span className="stat-label">Pending Payment</span></div>
              <div className="stat-card paid"><span className="stat-icon">⚙️</span><span className="stat-value">{orderStats.paid || 0}</span><span className="stat-label">Processing</span></div>
              <div className="stat-card shipped"><span className="stat-icon">🚚</span><span className="stat-value">{orderStats.shipped}</span><span className="stat-label">Shipped</span></div>
              <div className="stat-card delivered"><span className="stat-icon">✅</span><span className="stat-value">{orderStats.delivered}</span><span className="stat-label">Delivered</span></div>
              <div className="stat-card cancelled"><span className="stat-icon">❌</span><span className="stat-value">{orderStats.cancelled}</span><span className="stat-label">Cancelled</span></div>
              <div className="stat-card part-payment"><span className="stat-icon">💰</span><span className="stat-value">{orderStats.partPaymentOrders || 0}</span><span className="stat-label">Deposit Orders</span></div>
            </div>
            <div className="overview-actions">
              <button onClick={() => setActiveTab('orders')}>View Orders →</button>
              <button onClick={() => setActiveTab('products')}>Manage Products →</button>
              <button onClick={() => setActiveTab('users')}>Find Customer →</button>
              <Link to="/admin/chats">💬 Support Chats →</Link>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-table-container">
            <div className="admin-data-toolbar">
              <form className="admin-filter-form" onSubmit={(event) => {
                event.preventDefault();
                setAppliedProductSearchTerm(productSearchTerm.trim());
              }}>
                <input type="search" value={productSearchTerm} onChange={(event) => setProductSearchTerm(event.target.value)} placeholder="Search products..." aria-label="Search products" />
                <select value={productCategoryFilter} onChange={(event) => setProductCategoryFilter(event.target.value)}>
                  <option value="all">All Categories</option>
                  {['shoes','bags','handbags','travel','home','kitchen','school','clothing','accessories','wellness'].map(category => (
                    <option key={category} value={category}>{category.charAt(0).toUpperCase() + category.slice(1)}</option>
                  ))}
                </select>
                <select value={productStockFilter} onChange={(event) => setProductStockFilter(event.target.value)}>
                  <option value="all">All Stock</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
                <button type="submit">{productSearchLoading ? 'Loading...' : 'Search'}</button>
              </form>
              <span className="admin-result-count">{productPagination.totalProducts || 0} products</span>
            </div>
            <div className="table-responsive-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th className="desktop-only">Description</th>
                    <th>Category</th>
                    <th>Cost Price</th>
                    <th>Selling Price</th>
                    <th>Margin</th>
                    <th>Variations</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? products.map(product => {
                    return (
                      <tr key={product._id}>
                        <td><img src={getFullImageUrl(product.image)} alt={product.name} className="product-thumb" onError={(e) => (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="100%" height="100%" fill="%23f0f0f0"/%3E%3C/svg%3E'}/></td>
                        <td className="product-name-cell" title={product.name}>{truncateText(product.name, 25, 12)}</td>
                        <td className="product-description-cell desktop-only" title={product.description}>{truncateText(product.description, 40, 20)}</td>
                        <td>{product.category}</td>
                        <td className="cost-price-cell">₦{formatPrice(product.costPrice)}</td>
                        <td className="price-cell">{formatPrice(product.price)}</td>
                        <td className="margin-cell">{getProfitMarginDisplay(product)}</td>
                        <td>{getVariationBadges(product.variations)}</td>
                        <td>{getStockStatus(product)}</td>
                        <td className="actions-cell">
                          <button className="edit-btn" onClick={() => editProduct(product)}>Edit</button>
                          <button className="delete-btn" onClick={() => handleDeleteProduct(product._id)}>Delete</button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>No products found. Click "Add Product" to create one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {productPagination.totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={!productPagination.hasPreviousPage || productSearchLoading || productRefreshing}
                  onClick={() => setProductPage(page => Math.max(page - 1, 1))}
                >
                  ← Previous
                </button>
                <span>Page {productPagination.page} of {productPagination.totalPages}</span>
                <button
                  disabled={!productPagination.hasNextPage || productSearchLoading || productRefreshing}
                  onClick={() => setProductPage(page => page + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-list">
            <div className="admin-data-toolbar">
              <form className="admin-filter-form order-filter-form" onSubmit={(event) => {
                event.preventDefault();
                setAppliedOrderSearchTerm(orderSearchTerm.trim());
              }}>
                <input type="search" value={orderSearchTerm} onChange={(event) => setOrderSearchTerm(event.target.value)} placeholder="Order number, customer, email or phone..." aria-label="Search orders" />
                <select value={orderStatusFilter} onChange={(event) => setOrderStatusFilter(event.target.value)}>
                  <option value="all">All Statuses</option><option value="pending">Pending</option><option value="paid">Processing</option><option value="shipped">Shipped</option><option value="delivered">Delivered</option><option value="cancelled">Cancelled</option><option value="returned">Returned</option><option value="refunded">Refunded</option>
                </select>
                <select value={orderPaymentStatusFilter} onChange={(event) => setOrderPaymentStatusFilter(event.target.value)}>
                  <option value="all">All Payments</option><option value="pending">Payment Pending</option><option value="paid">Paid</option><option value="partial">Partial</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option>
                </select>
                <input type="date" value={orderStartDate} onChange={(event) => setOrderStartDate(event.target.value)} aria-label="Order start date" />
                <input type="date" value={orderEndDate} onChange={(event) => setOrderEndDate(event.target.value)} aria-label="Order end date" />
                <button type="submit">{orderSearchLoading ? 'Loading...' : 'Search'}</button>
              </form>
              <span className="admin-result-count">{orderPagination.totalOrders || 0} orders</span>
            </div>
            {orders.length > 0 ? orders.map(order => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <span className="admin-order-id">
                    Id: {order.orderReference || order._id.slice(-8)}
                  </span>
                  <span className="admin-order-date">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </span>
                  <div className="order-actions">
                    <select 
                      value={order.status} 
                      onChange={(e) => handleUpdateOrderStatus(order._id, e.target.value)} 
                      className={`order-status-select status-${order.status}`} 
                    >
                      {order.status === 'partial' && (
                        <option value="partial">Partial Payment</option>
                      )}
                      <option value="pending">Pending</option>
                      <option value="paid">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="returned">Returned</option>
                      <option value="refunded">Refunded</option>
                    </select>
                    
                    <button className="shipping-details-btn" onClick={() => setSelectedOrderForModal(order)}>
                      Details
                    </button>
                    <button 
                      className="receipt-btn-admin"
                      onClick={() => {
                        const orderForReceipt = {
                          ...order,
                          orderReference: order.orderReference || order._id.slice(-8)
                        };
                        setReceiptOrder(orderForReceipt as Order);
                      }}
                    >
                      Receipt
                    </button>
                  </div>
                </div>
                
                <div className="order-body">
                  <p>
                    <strong>Customer:</strong> {order.user?.name || 'Unknown'} ({order.user?.email})
                  </p>
                  <p>
                    <strong>Total:</strong> ₦{order.total?.toLocaleString()}
                  </p>
                  
                  <p>
                    <strong>Items:</strong> {order.items?.length || 0} products
                  </p>
                  <p>
                    <strong>Payment Status:</strong>{' '}
                    <span style={{
                      color:
                        order.paymentStatus === 'paid'
                          ? '#4caf50'
                          : order.paymentStatus === 'partial'
                            ? '#9c27b0'
                            : order.paymentStatus === 'cancelled'
                              ? '#f44336'
                              : '#ff9800',
                      fontWeight: 'bold'
                    }}>
                      {order.paymentStatus === 'paid'
                        ? '✓ PAID'
                        : order.paymentStatus === 'partial'
                          ? `PARTIAL — ₦${(order.remainingBalance || 0).toLocaleString()} BALANCE`
                          : order.paymentStatus === 'cancelled'
                            ? '✕ CANCELLED'
                            : order.paymentStatus === 'failed'
                              ? '✕ FAILED'
                              : 'PENDING'}
                    </span>
                  </p>
                  
                  {order.shippingAddress && (
                    <p>
                      <strong>Ship to:</strong> {order.shippingAddress.fullName}, {order.shippingAddress.city}, {order.shippingAddress.state}
                    </p>
                  )}
                </div>
              </div>
            )) : (
              <div className="empty-orders">No orders found</div>
            )}
            {orderPagination.totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={!orderPagination.hasPreviousPage || orderSearchLoading || orderRefreshing}
                  onClick={() => setOrderPage(page => Math.max(page - 1, 1))}
                >
                  ← Previous
                </button>
                <span>Page {orderPagination.page} of {orderPagination.totalPages}</span>
                <button
                  disabled={!orderPagination.hasNextPage || orderSearchLoading || orderRefreshing}
                  onClick={() => setOrderPage(page => page + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Shipping Label Modal */}
        {selectedOrderForModal && (
          <div 
            className="admin-modal-overlay" 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => setSelectedOrderForModal(null)}
          >
            <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal-content-child">
                <h3>📦 Shipping Label</h3>
                <button onClick={() => setSelectedOrderForModal(null)}>×</button>
              </div>
              
              <div id="shipping-content">
                <div className="shipping-content-details">
                  <img src="/Gimbiya.png" alt="Gimbiya" className="logo-image"/>
                  <h2>Gimbiya Value Store</h2>
                  <p>Zango, Zaria, Kaduna State, Nigeria</p>
                  <p>📞 +234 701 011 0977</p>
                  <p>✉️ hello@gimbiyastore.com</p>
                  <p>🌍 www.gimbiyastore.com</p>
                </div>

                <div className="order-info-details">
                  <h4>Order #{selectedOrderForModal.orderReference || selectedOrderForModal._id.slice(-8)}</h4>
                  <p>Date: {new Date(selectedOrderForModal.createdAt).toLocaleDateString()}</p>
                  <p>Status: {selectedOrderForModal.status.toUpperCase()}</p>
                </div>

                <div className="shipping-address">
                  <h4>🚚 SHIP TO:</h4>
                  <p><strong>{selectedOrderForModal.shippingAddress?.fullName || selectedOrderForModal.user?.name}</strong></p>
                  <p>{selectedOrderForModal.shippingAddress?.address}</p>
                  <p>{selectedOrderForModal.shippingAddress?.city}, {selectedOrderForModal.shippingAddress?.state}</p>
                  {selectedOrderForModal.shippingAddress?.postalCode && 
                  <p>Postal Code: {selectedOrderForModal.shippingAddress.postalCode}</p>}
                  <p>📞 Phone: {selectedOrderForModal.shippingAddress?.phone}</p>
                </div>

                <div className="items-summary">
                  <h4>📦 Items ({selectedOrderForModal.items?.length || 0})</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrderForModal.items?.slice(0, 5).map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.name}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.quantity}</td>
                        </tr>
                      ))}
                      {selectedOrderForModal.items?.length > 5 && (
                        <tr>
                          <td colSpan={2} style={{ border: '1px solid #ddd', padding: '8px' }}>
                            ... and {selectedOrderForModal.items.length - 5} more items
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="barcode-order-num">
                  {selectedOrderForModal.orderReference || selectedOrderForModal._id}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={() => {
                    const printContent = document.getElementById('shipping-content');
                    if (printContent) {
                      const iframe = document.createElement('iframe');
                      iframe.style.position = 'absolute';
                      iframe.style.width = '0';
                      iframe.style.height = '0';
                      iframe.style.border = 'none';
                      document.body.appendChild(iframe);
                      
                      const iframeDoc = iframe.contentWindow?.document;
                      if (iframeDoc) {
                        iframeDoc.open();
                        iframeDoc.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Shipping Label - ${selectedOrderForModal.orderReference || selectedOrderForModal._id}</title>
                            <style>
                              * { margin: 0; padding: 0; box-sizing: border-box; }
                              body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 20px; background: white; }
                              .shipping-label-container { max-width: 800px; margin: 0 auto; }
                              .shipping-content-details { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #333; }
                              .shipping-content-details h2 { font-size: 24px; margin-bottom: 10px; }
                              .shipping-content-details p { margin: 5px 0; font-size: 12px; }
                              .order-info-details { margin-bottom: 25px; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }
                              .order-info-details h4 { margin-bottom: 10px; }
                              .shipping-address { margin-bottom: 25px; padding: 15px; border: 1px solid #ddd; }
                              .shipping-address h4 { margin-bottom: 10px; color: #d32f2f; }
                              .shipping-address p { margin: 5px 0; font-size: 13px; }
                              .items-summary { margin-bottom: 25px; }
                              .items-summary h4 { margin-bottom: 10px; }
                              .items-summary table { width: 100%; border-collapse: collapse; }
                              .items-summary th, .items-summary td { padding: 8px; text-align: left; border: 1px solid #ddd; }
                              .items-summary th { background: #f0f0f0; }
                              .barcode-order-num { margin-top: 30px; padding: 15px; text-align: center; font-family: monospace; border-top: 1px dashed #999; }
                              @media print { body { margin: 0; padding: 0; } .no-print { display: none; } }
                            </style>
                          </head>
                          <body>
                            <div class="shipping-label-container">
                              ${printContent.innerHTML}
                            </div>
                            <div class="no-print" style="text-align: center; margin-top: 20px; padding: 10px; color: #666;">
                              <p>✅ This label is ready for printing. Use your device's print function (Ctrl+P or Cmd+P)</p>
                            </div>
                          </body>
                          </html>
                        `);
                        iframeDoc.close();
                        
                        setTimeout(() => {
                          try {
                            iframe.contentWindow?.print();
                            setTimeout(() => {
                              document.body.removeChild(iframe);
                            }, 1000);
                          } catch (error) {
                            console.error('Print failed:', error);
                            alert('Unable to print directly. You can take a screenshot or use the share button below.');
                            document.body.removeChild(iframe);
                          }
                        }, 500);
                      } else {
                        alert('Unable to open print dialog. Please take a screenshot or use the share button.');
                      }
                    }
                  }}
                  style={{ 
                    flex: 1, 
                    padding: '10px', 
                    background: '#4caf50', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    WebkitAppearance: 'none'
                  }}
                >
                  🖨️ Print Label
                </button>
                <button 
                  onClick={async (e) => {
                    const printContent = document.getElementById('shipping-content');
                    if (!printContent) return;

                    const isCapacitor = !!(window as any).Capacitor;
                    const orderId = selectedOrderForModal.orderReference || selectedOrderForModal._id;
                    
                    const button = e.currentTarget as HTMLButtonElement;
                    const originalButtonText = '📸 Share as Image';
                    button.innerHTML = '⏳ Capturing...';
                    button.disabled = true;

                    try {
                      const canvas = await html2canvas(printContent, {
                        scale: 3,
                        backgroundColor: '#ffffff',
                        useCORS: true,
                        logging: false,
                        windowWidth: 800,
                        windowHeight: printContent.scrollHeight
                      });
                      
                      const blob = await new Promise<Blob>((resolve) => {
                        canvas.toBlob((blob: Blob | null) => resolve(blob!), 'image/png', 1.0);
                      });
                      
                      if (isCapacitor) {
                        const base64 = await new Promise<string>((resolve) => {
                          const reader = new FileReader();
                          reader.onloadend = () => resolve(reader.result as string);
                          reader.readAsDataURL(blob);
                        });
                        
                        const fileName = `shipping-label-${orderId}.png`;
                        await Filesystem.writeFile({
                          path: fileName,
                          data: base64.split(',')[1],
                          directory: Directory.Cache
                        });
                        
                        const fileUri = await Filesystem.getUri({
                          path: fileName,
                          directory: Directory.Cache
                        });
                        
                        await Share.share({
                          title: 'Shipping Label',
                          text: `Order #${orderId}`,
                          url: fileUri.uri,
                          dialogTitle: 'Share Shipping Label'
                        });
                        
                        await Filesystem.deleteFile({
                          path: fileName,
                          directory: Directory.Cache
                        });
                        
                        alert('Label shared successfully!');
                      } else {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `shipping-label-${orderId}.png`;
                        a.click();
                        URL.revokeObjectURL(url);
                        alert('Image downloaded!');
                      }
                    } catch (error: any) {
                      console.error('Share failed:', error);
                      if (!error?.message?.includes('canceled')) {
                        alert('Unable to share. Please take a screenshot.');
                      }
                    } finally {
                      button.innerHTML = originalButtonText;
                      button.disabled = false;
                    }
                  }}
                  style={{ 
                    flex: 1, 
                    padding: '10px', 
                    background: '#ff9800', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    WebkitAppearance: 'none'
                  }}
                >
                  📸 Share as Image
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="customer-lookup">
            <div className="customer-lookup-header">
              <div>
                <h2>Customer Lookup</h2>
                <p>Search for a customer by name, email, or phone number.</p>
              </div>
            </div>

            <form
              className="customer-search-form"
              onSubmit={handleCustomerSearch}
            >
              <input
                type="search"
                value={userSearchTerm}
                onChange={(event) => setUserSearchTerm(event.target.value)}
                placeholder="Search name, email, or phone..."
                aria-label="Search customers"
              />

              <button
                type="submit"
                disabled={
                  customerSearchLoading ||
                  !userSearchTerm.trim()
                }
              >
                {customerSearchLoading ? 'Searching...' : 'Search'}
              </button>
            </form>

            {!customerSearchPerformed ? (
              <div className="customer-lookup-empty">
                <div className="customer-lookup-empty-icon">👤</div>
                <h3>Search for a customer</h3>
                <p>
                  Customers are not loaded automatically.
                  Enter a name, email, or phone number to find a customer.
                </p>
              </div>
            ) : customerSearchLoading ? (
              <div className="customer-lookup-loading">
                Searching customers...
              </div>
            ) : users.length === 0 ? (
              <div className="customer-lookup-empty">
                <div className="customer-lookup-empty-icon">🔍</div>
                <h3>No customers found</h3>
                <p>
                  Try another name, email address, or phone number.
                </p>
              </div>
            ) : (
              <div className="customer-results">
                {users.map((user) => (
                  <div
                    key={user._id}
                    className="customer-result-card"
                  >
                    <div className="customer-result-info">
                      <h3>{user.name}</h3>

                      <p>{user.email}</p>

                      {user.phone && (
                        <p>{user.phone}</p>
                      )}

                      <span>
                        Customer since{' '}
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleViewUserDetails(user)}
                      className="customer-view-button"
                    >
                      View Customer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'stock' && <StockManager />}
        {activeTab === 'sales' && <SalesAnalytics />}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div className="admin-modal-overlay main-modal" onClick={() => setShowProductModal(false)}>
          <div className="admin-modal-content admin-modal" onClick={e => e.stopPropagation()}>
            <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
            
            <div className="admin-form-grp"><label>Product Image</label><input type="file" accept="image/*" onChange={handleImageSelect} />{imagePreview && <img src={imagePreview} alt="Preview" className="image-preview" />}</div>
            <div className="admin-form-grp"><label>Product Name</label><input type="text" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required /></div>
            <div className="admin-form-grp"><label>Description</label><textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} rows={4} /></div>
            
            {/* Pricing Section */}
            <div className="pricing-section">
              <h4>💰 Pricing Configuration</h4>
              
              <div className="form-row">
                <div className="admin-form-grp">
                  <label>Cost Price (₦) *</label>
                  <input 
                    type="number" 
                    value={costPrice} 
                    onChange={(e) => handleCostPriceChange(e.target.value)} 
                    placeholder="How much you paid for the item"
                    required
                  />
                  <small>Your purchase cost</small>
                </div>
              </div>

              <div className="profit-margin-selector">
                <label>Profit Margin</label>
                <div className="margin-buttons">
                  <button type="button" className={`margin-btn ${!manualPriceMode && profitMargin === 5 ? 'active' : ''}`} onClick={() => handleProfitMarginChange(5)}>5% <span className="margin-desc">(Minimum)</span></button>
                  <button type="button" className={`margin-btn ${!manualPriceMode && profitMargin === 10 ? 'active' : ''}`} onClick={() => handleProfitMarginChange(10)}>10% <span className="margin-desc">(Standard)</span></button>
                  <button type="button" className={`margin-btn ${!manualPriceMode && profitMargin === 15 ? 'active' : ''}`} onClick={() => handleProfitMarginChange(15)}>15% <span className="margin-desc">(Premium)</span></button>
                  <button type="button" className={`margin-btn ${!manualPriceMode && profitMargin === 20 ? 'active' : ''}`} onClick={() => handleProfitMarginChange(20)}>20% <span className="margin-desc">(Luxury)</span></button>
                  <button type="button" className={`margin-btn ${!manualPriceMode && profitMargin === 30 ? 'active' : ''}`} onClick={() => handleProfitMarginChange(30)}>30% <span className="margin-desc">(High-end)</span></button>
                </div>
                
                <div className="custom-margin">
                  <label>Custom Margin (%):</label>
                  <input type="number" value={profitMargin} onChange={(e) => handleProfitMarginChange(parseFloat(e.target.value) || 0)} min="0" max="100" step="1" />
                </div>
              </div>

              <div className="calculated-price">
                <label>Calculated Selling Price:</label>
                <div className="price-display">
                  <span className="price-amount">₦{formatPrice(calculatedPrice)}</span>
                  {calculatedPrice > 0 && !manualPriceMode && (
                    <span className="price-note">(Cost + {profitMargin}% margin = ₦{formatPrice(calculatedPrice - parseFloat(costPrice || '0'))} profit)</span>
                  )}
                </div>
              </div>

              <div className="manual-price-toggle">
                <label>
                  <input type="checkbox" checked={manualPriceMode} onChange={(e) => setManualPriceMode(e.target.checked)} />
                  Set custom selling price manually
                </label>
              </div>

              {manualPriceMode && (
                <div className="admin-form-grp">
                  <label>Manual Selling Price (₦)</label>
                  <input type="number" value={manualPrice} onChange={(e) => handleManualPriceChange(e.target.value)} placeholder="Enter custom selling price" />
                  {parseFloat(costPrice) > 0 && parseFloat(manualPrice) > 0 && (
                    <small>Profit: ₦{(parseFloat(manualPrice) - parseFloat(costPrice)).toLocaleString()} (Margin: {(((parseFloat(manualPrice) - parseFloat(costPrice)) / parseFloat(manualPrice)) * 100).toFixed(1)}%)</small>
                  )}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="admin-form-grp">
                <label>Discount (%)</label>
                <input type="number" value={productForm.discount} onChange={(e) => setProductForm({...productForm, discount: e.target.value})} placeholder="0" min="0" max="100" />
                <small>Discount will be applied to selling price for promotions</small>
              </div>
              <div className="admin-form-grp">
                <label>Final Price After Discount</label>
                <input type="text" value={(() => {
                  const price = manualPriceMode ? parseFloat(manualPrice) : calculatedPrice;
                  const discount = parseFloat(productForm.discount) || 0;
                  const finalPrice = price * (1 - discount / 100);
                  return `₦${finalPrice.toLocaleString()}`;
                })()} disabled className="final-price-display" />
              </div>
            </div>

            <div className="form-row">
              <div className="admin-form-grp">
                <label>Category</label>
                <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})}>
                  <option value="shoes">👟 Shoes</option>
                  <option value="bags">👜 Bags</option>
                  <option value="handbags">👜 Handbags</option>
                  <option value="travel">🧳 Travel</option>
                  <option value="home">🏠 Home</option>
                  <option value="kitchen">🍳 Kitchen</option>
                  <option value="school">📚 School</option>
                  <option value="clothing">👕 Clothing</option>
                  <option value="accessories">💍 Accessories</option>
                  <option value="wellness">🧘 Wellness</option>
                </select>
              </div>
            </div>

            {/* Product Type Selection */}
            <div className="admin-form-grp">
              <label className="form-label">📦 Product Type</label>
              <div className="product-type-options">
                <label className={`type-option ${productType === 'simple' ? 'selected' : ''}`}>
                  <input type="radio" name="productType" value="simple" checked={productType === 'simple'} onChange={() => { setProductType('simple'); setVariations([]); }} />
                  <div className="type-content"><span className="type-icon">📦</span><div><strong>Simple Product</strong><small>No colors, no sizes</small></div></div>
                </label>
                <label className={`type-option ${productType === 'color_only' ? 'selected' : ''}`}>
                  <input type="radio" name="productType" value="color_only" checked={productType === 'color_only'} onChange={() => { setProductType('color_only'); }} />
                  <div className="type-content"><span className="type-icon">🎨</span><div><strong>Colors Only</strong><small>Multiple colors (no sizes)</small></div></div>
                </label>
                <label className={`type-option ${productType === 'color_size' ? 'selected' : ''}`}>
                  <input type="radio" name="productType" value="color_size" checked={productType === 'color_size'} onChange={() => { setProductType('color_size'); }} />
                  <div className="type-content"><span className="type-icon">🎨👟</span><div><strong>Colors + Sizes</strong><small>Multiple colors with sizes</small></div></div>
                </label>
              </div>
            </div>

            {/* Base Stock - Only for Simple Products */}
            {productType === 'simple' && (
              <div className="admin-form-grp"><label>📊 Stock Quantity</label><input type="number" value={productForm.stockQuantity} onChange={(e) => handleStockQuantityChange(e.target.value)} placeholder="Enter quantity" /></div>
            )}

            {/* Color Variations Section */}
            {(productType === 'color_only' || productType === 'color_size') && (
              <div className="variations-section">
                <div className="section-header"><label>🎨 Color Variations</label><button type="button" onClick={handleAddVariation} className="add-variation-btn">+ Add Color</button></div>
                {variations.length > 0 ? (
                  <div className="variations-list">
                    {variations.map((variation, idx) => (
                      <div key={idx} className="variation-item">
                        <div className="variation-color-preview" style={{ backgroundColor: variation.colorCode }} />
                        <div className="variation-info">
                          <strong>{variation.color}</strong>
                          {variation.additionalPrice > 0 && <span>+₦{variation.additionalPrice.toLocaleString()}</span>}
                          {variation.sizes && variation.sizes.length > 0 && (
                            <div className="variation-sizes">
                              {variation.sizes.map(size => (
                                <span key={size.ukSize} className="size-badge-small">UK {size.ukSize}: {size.stockQuantity}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="variation-images-preview">
                          {variation.images.slice(0, 2).map((img, imgIdx) => (
                            <img key={imgIdx} src={getFullImageUrl(img)} alt={variation.color} />
                          ))}
                          {variation.images.length > 2 && <span>+{variation.images.length - 2}</span>}
                        </div>
                        <div className="variation-actions">
                          <button onClick={() => handleEditVariation(idx)}>✏️</button>
                          <button onClick={() => handleRemoveVariation(idx)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (<div className="no-variations"><p>No colors added yet. Click "Add Color" to add different color options.</p></div>)}
              </div>
            )}

            {/* Dimension Section */}
            <div className="admin-form-grp">
              <label>📏 Dimensions (Optional)</label>
              <div className="dimension-controls">
                <div className="dimension-checkbox">
                  <label><input type="checkbox" checked={dimensionEnabled.length} onChange={(e) => setDimensionEnabled({...dimensionEnabled, length: e.target.checked})} />Length</label>
                  {dimensionEnabled.length && <input type="number" value={dimension.length || ''} onChange={(e) => handleDimensionChange('length', e.target.value)} placeholder="Length" step="0.01" />}
                </div>
                <div className="dimension-checkbox">
                  <label><input type="checkbox" checked={dimensionEnabled.breadth} onChange={(e) => setDimensionEnabled({...dimensionEnabled, breadth: e.target.checked})} />Breadth</label>
                  {dimensionEnabled.breadth && <input type="number" value={dimension.breadth || ''} onChange={(e) => handleDimensionChange('breadth', e.target.value)} placeholder="Breadth" step="0.01" />}
                </div>
                <div className="dimension-checkbox">
                  <label><input type="checkbox" checked={dimensionEnabled.height} onChange={(e) => setDimensionEnabled({...dimensionEnabled, height: e.target.checked})} />Height</label>
                  {dimensionEnabled.height && <input type="number" value={dimension.height || ''} onChange={(e) => handleDimensionChange('height', e.target.value)} placeholder="Height" step="0.01" />}
                </div>
                {(dimensionEnabled.length || dimensionEnabled.breadth || dimensionEnabled.height) && (
                  <div className="dimension-unit">
                    <label>Unit:</label>
                    <select value={dimension.unit} onChange={(e) => handleDimensionUnitChange(e.target.value as 'cm' | 'm' | 'inch')}>
                      <option value="cm">cm</option>
                      <option value="m">m</option>
                      <option value="inch">inch</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Rim Size for Travel */}
            {productForm.category === 'travel' && (
              <div className="admin-form-grp"><label>🚗 Rim Size</label><input type="text" value={rimSize} onChange={(e) => setRimSize(e.target.value)} placeholder="e.g., 15-inch, 16-inch, 17x7.5" /></div>
            )}

            {/* Age Range */}
            <div className="admin-form-grp">
              <label><input type="checkbox" checked={enableAgeRange} onChange={(e) => setEnableAgeRange(e.target.checked)} /> 🎂 Set Age Range (Optional)</label>
              {enableAgeRange && (
                <div className="age-range-controls">
                  <div className="age-input"><label>Min Age:</label><input type="number" value={ageRange.min} onChange={(e) => handleAgeRangeChange('min', e.target.value)} min="0" max="120" /></div>
                  <div className="age-input"><label>Max Age:</label><input type="number" value={ageRange.max} onChange={(e) => handleAgeRangeChange('max', e.target.value)} min="0" max="120" /></div>
                </div>
              )}
            </div>

            {/* Related Categories */}
            <div className="admin-form-grp">
              <label className="form-label">🔗 Related Categories</label>
              <div className="related-categories-container">
                <p className="helper-text">Select product categories that are related to this item.</p>
                <div className="categories-checkbox-group">
                  {['shoes', 'bags', 'handbags', 'travel', 'home', 'kitchen', 'school', 'clothing', 'accessories', 'wellness'].filter(cat => cat !== productForm.category).map(category => (
                    <label key={category} className="category-checkbox">
                      <input type="checkbox" checked={relatedCategories.includes(category)} onChange={() => { 
                        if (relatedCategories.includes(category)) { 
                          setRelatedCategories(relatedCategories.filter(c => c !== category)); 
                        } else { 
                          setRelatedCategories([...relatedCategories, category]); 
                        } 
                      }} />
                      <span className="category-name">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                    </label>
                  ))}
                </div>
                {relatedCategories.length > 0 && (
                  <div className="selected-categories-info">
                    <strong>✅ Selected for recommendations:</strong>
                    <div className="selected-tags">
                      {relatedCategories.map(cat => (
                        <span key={cat} className="category-tag">
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          <button type="button" className="remove-tag" onClick={() => setRelatedCategories(relatedCategories.filter(c => c !== cat))}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="admin-form-grp-special">
                <label><input type="checkbox" checked={productForm.isFeatured} onChange={(e) => setProductForm({...productForm, isFeatured: e.target.checked})} /> Featured</label>
                <label><input type="checkbox" checked={productForm.isOverseas} onChange={(e) => setProductForm({...productForm, isOverseas: e.target.checked})} /> ✈️ Overseas</label>
              </div>
            </div>

            <div className="admin-modal-buttons">
              <button onClick={handleSaveProduct}>💾 Save Product</button>
              <button onClick={() => setShowProductModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Variation Modal */}
      {showVariationModal && (
        <div className="admin-modal-overlay" onClick={() => setShowVariationModal(false)}>
          <div className="admin-modal-content variation-modal" onClick={e => e.stopPropagation()}>
            <h3>{editingVariationIndex !== null ? 'Edit Color Variation' : 'Add Color Variation'}</h3>
            
            <div className="admin-form-grp"><label>Color Name</label><input type="text" value={variationForm.color} onChange={(e) => setVariationForm({...variationForm, color: e.target.value})} placeholder="e.g., Red, Blue, Black, White" required /></div>
            <div className="admin-form-grp"><label>Color Code</label><div className="color-picker-row"><input type="color" value={variationForm.colorCode} onChange={(e) => setVariationForm({...variationForm, colorCode: e.target.value})} /><input type="text" value={variationForm.colorCode} onChange={(e) => setVariationForm({...variationForm, colorCode: e.target.value})} placeholder="#000000" /></div></div>
            <div className="admin-form-grp"><label>Additional Price (₦)</label><input type="number" value={variationForm.additionalPrice} onChange={(e) => setVariationForm({...variationForm, additionalPrice: e.target.value})} placeholder="0" /><small>Extra cost for this color</small></div>
            <div className="form-row"><div className="admin-form-grp"><label><input type="checkbox" checked={variationForm.isDefault} onChange={(e) => setVariationForm({...variationForm, isDefault: e.target.checked})} /> 🌟 Set as Default Color</label></div></div>
            
            {/* Sizes Section */}
            {productType === 'color_size' && (
              <div className="admin-form-grp">
                <label>👟 Sizes for {variationForm.color || 'this color'}</label>
                <div className="shoe-sizes-container">
                  <div className="size-selector" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <select value={selectedSizeToAdd} onChange={(e) => setSelectedSizeToAdd(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                      <option value="">Select size to add</option>
                      {UK_SHOE_SIZES_WITH_FOOT_LENGTH.map(sizeInfo => (
                        <option key={sizeInfo.ukSize} value={sizeInfo.ukSize} disabled={variationSizes.some(s => s.ukSize === sizeInfo.ukSize)}>
                          UK {sizeInfo.ukSize} - {sizeInfo.footLengthCm}cm
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={handleOpenAddSizeModal} style={{ padding: '8px 16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Add Size</button>
                  </div>
                  
                  {variationSizes.length > 0 && (
                    <div className="selected-sizes-with-stock">
                      <h4>Added Sizes:</h4>
                      {variationSizes.map((sizeInfo, idx) => (
                        <div key={idx} className="size-stock-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', margin: '5px 0', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          <div className="size-info">
                            <span className="size-number" style={{ fontWeight: 'bold', marginRight: '10px' }}>UK {sizeInfo.ukSize}</span>
                            <span className="size-cm" style={{ marginRight: '10px', color: '#666' }}>{sizeInfo.footLengthCm}cm</span>
                            <span className={`size-stock ${sizeInfo.stockQuantity === 0 ? 'out-of-stock' : 'in-stock'}`}>Stock: {sizeInfo.stockQuantity}</span>
                          </div>
                          <div className="size-actions">
                            <button onClick={() => handleEditSize(idx)} style={{ marginRight: '5px', padding: '4px 8px', cursor: 'pointer' }}>✏️</button>
                            <button onClick={() => handleRemoveSize(idx)} style={{ padding: '4px 8px', cursor: 'pointer' }}>🗑️</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Images */}
            <div className="admin-form-grp">
              <label>Images for this color</label>
              <input type="file" accept="image/*" multiple onChange={handleVariationImageSelect} />
              <small>Select multiple images for this color variation</small>
              {variationImagePreviews.length > 0 && (
                <div className="variation-images-grid">
                  {variationImagePreviews.map((preview, idx) => (
                    <div key={idx} className="variation-image-item">
                      <img src={preview.startsWith('data:') ? preview : getFullImageUrl(preview)} alt={`Preview ${idx}`} />
                      <button onClick={() => removeVariationImage(idx)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="admin-modal-buttons">
              <button onClick={saveVariation}>💾 Save Variation</button>
              <button onClick={() => setShowVariationModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Size Modal */}
      {showSizeModal && (
        <div className="admin-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSizeModal(false)}>
          <div className="admin-modal-content size-stock-modal" style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', minWidth: '300px', maxWidth: '400px', zIndex: 10001 }} onClick={e => e.stopPropagation()}>
            <h3>{editingSizeIndex !== null ? 'Edit Size' : 'Add Size'}</h3>
            <div className="admin-form-grp">
              <label>UK Size</label>
              <input type="text" value={`UK ${sizeForm.ukSize} (${sizeForm.footLengthCm}cm)`} disabled className="disabled-input" style={{ width: '100%', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} />
            </div>
            <div className="admin-form-grp">
              <label>Stock Quantity</label>
              <input type="number" value={sizeForm.stockQuantity} onChange={(e) => setSizeForm({...sizeForm, stockQuantity: parseInt(e.target.value) || 0})} min="0" placeholder="Enter stock quantity" autoFocus style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
            </div>
            <div className="admin-modal-buttons" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleSaveSize} style={{ padding: '8px 16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>💾 Save Size</button>
              <button onClick={() => setShowSizeModal(false)} style={{ padding: '8px 16px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          orders={selectedUserOrders}
          pointsHistory={selectedUserPoints}
          loading={loadingUserDetails}
          onClose={() => {
            setSelectedUser(null);
            setSelectedUserOrders([]);
            setSelectedUserPoints([]);
          }}
        />
      )}
      
      {/* Receipt Modal */}
      {receiptOrder && (
        <ReceiptModal
          order={receiptOrder}
          onClose={() => setReceiptOrder(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;